import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Observable, Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {Centre} from "../../../../interfaces/Centres";
import {MatTableDataSource} from "@angular/material/table";
import {MatPaginator} from "@angular/material/paginator";
import {AngularFirestore} from "@angular/fire/compat/firestore";
import {CentreService} from "../../../../services/centre.service";
import {LoadingService} from "../../../../services/loading.service";
import * as XLSX from "xlsx";
import {OccupancyReport} from "../occupancy-reports/occupancy-reports.component";
import {PermissionType} from "../../../../components/employee/PermissionType.enum";
import {FinancialYearService} from "../../../../services/financial-year.service";

interface DailyOccupancyReport {
  centreCode: string;
  children: Childs[];
  date: any;
}

interface Childs {
  childCode: string;
  firstName: string;
  lastName: string;
  currentUnit: string;
}

@Component({
  selector: 'app-daily-child-occupancy',
  templateUrl: './daily-child-occupancy.component.html',
  styleUrls: ['./daily-child-occupancy.component.scss']
})
export class DailyChildOccupancyComponent implements OnInit, OnDestroy {
  private _destroyed$ = new Subject<void>();
  startDate: any;
  endDate: any;
  // selectedCentreCode: any = '';

  selectedCentreCode: string[] = [];   // supports multi-select
  selectedCentre: Centre[] = [];

  centres: Centre[] = [];
  dailyReport: DailyOccupancyReport[] = [];
  centres$!: Observable<Centre[]>;
  dataSource!: MatTableDataSource<DailyOccupancyReport>;
  displayedColumns: string[] = ['Sr', 'centreName', 'date', 'childDetails'];
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  constructor(private firestore: AngularFirestore,
              private mCentreService: CentreService,
              private loadingService: LoadingService,
              private readonly financialYearService: FinancialYearService,) {
    this.centres$ = this.mCentreService.getAllCentres();
  }

  ngOnInit(): void {
    const fy = this.financialYearService.getCurrentFinancialYear();
    this.startDate = fy.startDate;
    this.endDate = fy.endDate;
    this.centres$.pipe(takeUntil(this._destroyed$)).subscribe((centres) => {
      this.centres = centres;
    });
    this.dailyReport = [...this.dataSource.data];
  }

  // Add this method for displayWithFn
  displayCentre = (centre: Centre): string => {
    return centre ? centre.centreName : '';
  }



  // Method to get centreName by centreCode
  getCentreNameByCode(centreCode: string): string {
    const centre = this.centres.find(c => c.centreCode === centreCode);
    return centre ? centre.centreName : 'Unknown Centre';  // Return 'Unknown Centre' if not found
  }

  onCentreSelect(selectedCentre: Centre | Centre[]) {

    if (!selectedCentre || (Array.isArray(selectedCentre) && selectedCentre.length === 0)) {
      this.selectedCentre = [];
      this.selectedCentreCode = [];
      return;
    }

    // Normalize to array
    const centres = Array.isArray(selectedCentre) ? selectedCentre : [selectedCentre];

    this.selectedCentre = centres;
    this.selectedCentreCode = centres.map(c => c.centreCode);

    console.log('Selected Centres:', this.selectedCentreCode);
  }


  SearchData() {
    this.loadingService.setLoading(true);

    // ✅ Only validate dates (centre is optional now)
    if (!this.startDate || !this.endDate) {
      alert('Please select a valid date range.');
      this.loadingService.setLoading(false);
      return;
    }

    this.firestore
      .collection<DailyOccupancyReport>('dailyChildOccupancy', ref =>
        ref.orderBy('date', 'desc')
      )
      .valueChanges()
      .pipe(takeUntil(this._destroyed$))
      .subscribe(data => {

        const adjustedEndDate = new Date(this.endDate);
        adjustedEndDate.setHours(23, 59, 59, 999);

        const filteredData = data.filter((res: DailyOccupancyReport) => {
          const reportDate = res.date.toDate().getTime();

          const isDateMatched =
            reportDate >= this.startDate.getTime() &&
            reportDate <= adjustedEndDate.getTime();

          // ✅ MULTI / ALL centre logic
          const isCenterMatched =
            this.selectedCentreCode.length === 0 ||   // ALL centres
            this.selectedCentreCode.includes(res.centreCode);

          return isDateMatched && isCenterMatched;
        });

        this.dailyReport = filteredData;
        this.dataSource = new MatTableDataSource(this.dailyReport);
        this.dataSource.paginator = this.paginator;

        this.loadingService.setLoading(false);
      });
  }


  clearAll() {
    const fy = this.financialYearService.getCurrentFinancialYear();
    this.startDate = fy.startDate;
    this.endDate = fy.endDate;
    this.dailyReport = [];
    this.dataSource = new MatTableDataSource(this.dailyReport);
    this.dataSource.paginator = this.paginator;
    this.selectedCentreCode = []
    this.selectedCentre=[]
  }

  protected readonly Math = Math;

  applyFilter(event: Event): void {
    if (!this.dataSource || !this.dataSource.data) {
      return;
    }
    const globalFilter = (event.target as HTMLInputElement).value.trim().toLowerCase(); // Get the filter value
    if (!globalFilter) {
      this.dataSource.data = [...this.dailyReport];
    } else {
      const filteredData = this.dailyReport.map(report => ({
        ...report,
        children: report.children.filter(child =>
          child.firstName.toLowerCase().includes(globalFilter) ||
          child.lastName.toLowerCase().includes(globalFilter) ||
          child.childCode.toLowerCase().includes(globalFilter) ||
          this.getCentreNameByCode(report.centreCode).toLowerCase().includes(globalFilter) || // Example: Filtering by centreName
          report.date.toDate().toLocaleDateString().toLowerCase().includes(globalFilter) // Example: Filtering by date
        )
      })).filter(report => report.children.length > 0);

      this.dataSource.data = filteredData;
    }

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  exportexcel() {
    const dataToExport: any[] = []; // Initialize as an empty ARRAY

    this.dataSource.data.forEach((centreData: DailyOccupancyReport) => {
      centreData.children.forEach((child: Childs, childIndex: number) => {
        dataToExport.push({  // Now push will work correctly
          'Sr. No.': childIndex + 1,
          'Centre Name': this.getCentreNameByCode(centreData.centreCode),
          'Date': centreData.date.toDate(),
          'Child Name': `${child.firstName} ${child.lastName}`,
          'Child Code': child.childCode,
          'Child Unit': child.currentUnit
        });
      });
    });

    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(dataToExport);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Daily Occupancy Report');

    XLSX.writeFile(wb, 'daily_occupancy_report.xlsx');
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  protected readonly PermissionType = PermissionType;
}
